import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, GripVertical, Megaphone } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/components/AuthGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { InstructionBanner, PageTitle, Pill, EmptyState, LoadingRows, SearchBar, searchMatch } from "@/components/Bits";
import { TF, f, TASK_STATUSES, TASK_PRIORITIES, TASK_CATEGORIES } from "@/lib/fields";
import { fmtDate, isOverdue } from "@/lib/format";
import { setTaskAudienceApi, profileTaskApi, profileTaskDoneApi, apiErrorMessage } from "@/lib/api";
import { Link } from "react-router-dom";
import { UserRound, Check } from "lucide-react";

const ProfileTaskCard = () => {
  const [task, setTask] = useState(null);
  useEffect(() => {
    profileTaskApi().then(setTask).catch(() => {});
  }, []);
  if (!task || task.status !== "open") return null;
  const markDone = async () => {
    try {
      await profileTaskDoneApi();
      setTask((t) => ({ ...t, status: "done" }));
      toast.success("Nice — first task done.");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };
  return (
    <div data-testid="profile-task-card" className="border border-accent/40 bg-accent/10 rounded-lg p-4 mb-4 flex flex-wrap items-center gap-3">
      <div className="w-9 h-9 rounded-lg bg-accent/15 border border-accent/30 flex items-center justify-center shrink-0">
        <UserRound className="w-4 h-4 text-accent-ink" />
      </div>
      <div className="flex-1 min-w-[220px]">
        <p className="font-display font-bold text-primary">Your first task: set up your profile</p>
        <p className="text-xs text-faint">Add a photo, a nickname, and a fun fact so the team knows who you are. It checks itself off when you save.</p>
      </div>
      <Button asChild size="sm" className="gap-1.5 bg-accent hover:bg-accent-press" data-testid="profile-task-open-btn">
        <Link to={`/team/${task.user_id}`}><UserRound className="w-3.5 h-3.5" /> Open my profile</Link>
      </Button>
      <Button data-testid="profile-task-done-btn" variant="outline" size="sm" className="gap-1.5" onClick={markDone}>
        <Check className="w-3.5 h-3.5" /> Mark done
      </Button>
    </div>
  );
};

const GROUPS = [
  { key: "sales", label: "Sales" },
  { key: "marketing", label: "Marketing" },
  { key: "crew", label: "Crew" },
];
const ALL_GROUPS = GROUPS.map((g) => g.key);

const AudienceChips = ({ value, onToggle, testPrefix }) => (
  <div className="flex flex-wrap items-center gap-1">
    {GROUPS.map((g) => {
      const on = value.includes(g.key);
      return (
        <button
          key={g.key}
          type="button"
          data-testid={`${testPrefix}-${g.key}`}
          onClick={() => onToggle(g.key)}
          className={`text-[10px] font-semibold rounded-full border px-2 py-0.5 transition-colors ${
            on ? "bg-primary text-white border-primary" : "bg-surface text-faint border-border hover:border-border-strong"
          }`}
        >
          {g.label}
        </button>
      );
    })}
  </div>
);

const TaskCard = ({ task, isOwner, onToggleAudience }) => {
  const { updateRecord } = useApp();
  const overdue = isOverdue(f(task, TF.dueDate)) && f(task, TF.status) !== "Done";
  return (
    <div
      data-testid="task-card"
      draggable
      onDragStart={(e) => e.dataTransfer.setData("text/plain", task.id)}
      className="kanban-card surface p-3 space-y-2"
    >
      <div className="flex items-start gap-1.5">
        <GripVertical className="w-4 h-4 text-faint/70 mt-0.5 shrink-0" />
        <span className="text-sm font-medium leading-snug">{f(task, TF.task) || "Task"}</span>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {f(task, TF.priority) && <Pill value={f(task, TF.priority)} />}
        {f(task, TF.category) && (
          <span className="inline-flex items-center border border-primary/20 bg-primary/5 text-primary rounded-full px-2.5 py-0.5 text-xs font-semibold">
            {f(task, TF.category)}
          </span>
        )}
        {f(task, TF.dueDate) && (
          <span className={`text-xs font-semibold ${overdue ? "text-destructive" : "text-faint"}`}>
            {overdue ? "Late: " : "Due "}{fmtDate(f(task, TF.dueDate))}
          </span>
        )}
      </div>
      {isOwner && <AudienceChips value={task.audience || []} onToggle={(g) => onToggleAudience(task, g)} testPrefix="task-share" />}
      <div className="md:hidden">
        <Select value={f(task, TF.status) || "Backlog"} onValueChange={(v) => updateRecord("tasks", task.id, { [TF.status]: v }).catch(() => {})}>
          <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{TASK_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </div>
  );
};

const blank = { task: "", priority: "Medium", category: "Ops", dueDate: "", notes: "" };

export default function Tasks() {
  const { role } = useAuth();
  const isOwner = (role || "owner") === "owner";
  const { loadTable, records, tableState, updateRecord, createRecord } = useApp();
  const [dragOver, setDragOver] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(blank);
  const [audience, setAudience] = useState([]);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    loadTable("tasks");
  }, [loadTable]);

  const tasks = records("tasks").filter((t) => searchMatch(query, f(t, TF.task), f(t, TF.category), f(t, TF.priority), f(t, TF.notes)));
  const { loading, error } = tableState("tasks");

  const onDrop = (e, status) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData("text/plain");
    if (id) updateRecord("tasks", id, { [TF.status]: status }).catch(() => {});
  };

  const openNew = (aud) => {
    setForm(blank);
    setAudience(aud);
    setModalOpen(true);
  };

  const toggleFormAudience = (g) =>
    setAudience((a) => (a.includes(g) ? a.filter((x) => x !== g) : [...a, g]));

  const toggleCardAudience = async (task, group) => {
    const cur = task.audience || [];
    const next = cur.includes(group) ? cur.filter((g) => g !== group) : [...cur, group];
    try {
      await setTaskAudienceApi(task.id, next, f(task, TF.task) || "");
      await loadTable("tasks", true);
      toast.success(next.length ? `Shared with: ${next.join(", ")}.` : "Back to owner-only.");
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const save = async () => {
    if (!form.task.trim()) {
      toast.error("Write what the task is first.");
      return;
    }
    setSaving(true);
    try {
      const rec = await createRecord("tasks", {
        [TF.task]: form.task.trim(),
        [TF.status]: "To Do",
        [TF.priority]: form.priority,
        [TF.category]: form.category,
        [TF.dueDate]: form.dueDate,
        [TF.notes]: form.notes,
      });
      if (audience.length) {
        await setTaskAudienceApi(rec.id, audience, form.task.trim());
        await loadTable("tasks", true);
      }
      toast.success(
        audience.length === 3
          ? "Sent to everyone — Sales, Marketing, and Crew will all see it."
          : audience.length
          ? `Task added and shared with: ${audience.join(", ")}.`
          : "Task added to To Do."
      );
      setForm(blank);
      setAudience([]);
      setModalOpen(false);
    } catch {}
    setSaving(false);
  };

  return (
    <div data-testid="tasks-page">
      <PageTitle
        title="To-Do Board"
        subtitle={isOwner ? "Drag cards as work moves along." : "What the owner shared with your team."}
        action={
          isOwner ? (
            <div className="flex gap-2">
              <Button data-testid="mass-message-btn" variant="outline" onClick={() => openNew(ALL_GROUPS)} className="gap-1.5 border-primary/30 text-primary">
                <Megaphone className="w-4 h-4" /> Mass message
              </Button>
              <Button data-testid="new-task-btn" onClick={() => openNew([])} className="gap-1.5 bg-accent hover:bg-accent-press">
                <Plus className="w-4 h-4" /> Add task
              </Button>
            </div>
          ) : null
        }
      />
      <InstructionBanner>
        {isOwner
          ? "Drag cards between columns. Tap Sales / Marketing / Crew on a card to share it with that team — or use Mass message to hit everyone at once."
          : "These are the tasks shared with your team. Drag a card (or use the dropdown on a phone) as you get it done."}
      </InstructionBanner>

      <div className="mb-4">
        <SearchBar value={query} onChange={setQuery} placeholder="Search tasks…" testId="tasks-search-input" />
      </div>

      <ProfileTaskCard />

      {loading && !tasks.length ? (
        <LoadingRows />
      ) : error && !tasks.length ? (
        <EmptyState>{error}</EmptyState>
      ) : !isOwner && tasks.length === 0 ? (
        <EmptyState>{query ? "No tasks match that search." : "Nothing shared with your team yet. The owner assigns tasks from their To-Do board."}</EmptyState>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {TASK_STATUSES.map((status) => {
            const col = tasks.filter((t) => (f(t, TF.status) || "Backlog") === status);
            return (
              <div
                key={status}
                data-testid={`kanban-col-${status.toLowerCase().replace(/\s+/g, "-")}`}
                className={`kanban-col rounded-lg border border-border bg-surface-sunk/60 p-3 ${dragOver === status ? "drag-over" : ""}`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(status); }}
                onDragLeave={() => setDragOver(null)}
                onDrop={(e) => onDrop(e, status)}
              >
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-display font-bold text-sm text-primary">{status}</h2>
                  <span className="text-xs font-bold text-faint">{col.length}</span>
                </div>
                <div className="space-y-2">
                  {col.map((t) => <TaskCard key={t.id} task={t} isOwner={isOwner} onToggleAudience={toggleCardAudience} />)}
                  {col.length === 0 && <div className="text-xs text-faint text-center py-4">Drop tasks here</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent data-testid="new-task-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{audience.length === 3 ? "Mass message" : "Add task"}</DialogTitle>
            <DialogDescription>
              {audience.length === 3
                ? "This goes to Sales, Marketing, and Crew all at once."
                : "New tasks start in the To Do column."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>What needs doing? *</Label>
              <Input data-testid="task-name-input" value={form.task} onChange={(e) => setForm((s) => ({ ...s, task: e.target.value }))} />
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm((s) => ({ ...s, priority: v }))}>
                <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TASK_PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm((s) => ({ ...s, category: v }))}>
                <SelectTrigger data-testid="task-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TASK_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Due date</Label>
              <Input data-testid="task-due-input" type="date" value={form.dueDate} onChange={(e) => setForm((s) => ({ ...s, dueDate: e.target.value }))} />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea value={form.notes} onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))} rows={2} />
            </div>
            <div className="col-span-2">
              <Label>Who sees this?</Label>
              <div className="flex flex-wrap items-center gap-2 mt-1.5">
                <AudienceChips value={audience} onToggle={toggleFormAudience} testPrefix="new-task-share" />
                <button
                  type="button"
                  data-testid="new-task-share-everyone"
                  onClick={() => setAudience(audience.length === 3 ? [] : ALL_GROUPS)}
                  className={`text-[10px] font-semibold rounded-full border px-2 py-0.5 transition-colors ${
                    audience.length === 3 ? "bg-accent text-white border-accent" : "bg-surface text-accent-ink border-accent/40 hover:border-accent"
                  }`}
                >
                  Everyone
                </button>
              </div>
              <p className="text-[11px] text-faint mt-1">Leave them all off to keep it owner-only.</p>
            </div>
          </div>
          <Button data-testid="task-save-btn" onClick={save} disabled={saving} className="w-full gap-2 bg-accent hover:bg-accent-press">
            <Plus className="w-4 h-4" /> {saving ? "Saving…" : audience.length === 3 ? "Send to everyone" : "Save task"}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
